import { faCalendar } from "@fortawesome/free-regular-svg-icons/faCalendar";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { Dialog } from "@mui/material"
import { PaperArrastavel } from "app/components/dialog-arrastavel/PaperArrastavel"
import { DataFormatoEN } from "app/types/DataFormatoEN"
import { DataFormatoPT } from "app/types/DataFormatoPT"
import globalMU from "globalMU"
import React, { useEffect, useState, useRef, useCallback, useMemo } from "react"
import InputMask from '../../libs/reactInputMask'
import root from 'react-shadow'
import { Calendario, CLASSE_DIALOG_CALENDARIO } from "../calendario/Calendarios"
import { parse } from "date-fns"
import * as R from 'ramda'

type InputDataProps = {
    onChange?: (data: DataFormatoEN) => void
    data: DataFormatoPT
    className?: string
    selecionaValorOnFocus?: boolean
    disabled?: boolean
    id?: string
    bloquearDataMenorQueHoje?: boolean
    dataMinima?: { year: number, month: number, day: number }
    dataMaxima?: { year: number, month: number, day: number }
}

export const InputData = (props: InputDataProps) => {
    const isMounted = useRef(true)
    const inputId = useMemo(() => props.id ? `InputData_${props.id}` : `InputData_${Date.now()}`, [props.id])
    const iconId = useMemo(() => `${inputId}_icon`, [inputId])

    const [keyDown, setKeyDown] = useState(false)
    const [touch, setTouch] = useState(false)
    const [data, setData] = useState(props.data ?? "")
    const [mostrarCalendario, setMostrarCalendario] = useState(false)

    useEffect(() => {
        return () => {
            isMounted.current = false
        }
    }, [])

    useEffect(() => {
        if (isMounted.current) {
            setData(props.data ?? "")
        }
    }, [props.data])

    const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
        setKeyDown(true)
        if (R.includes(e.code, ["Backspace"]) && e.currentTarget.selectionStart === 0 && e.currentTarget.selectionEnd === 10) {
            setData("")
        }
    }, [])

    const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        if (!isMounted.current) return

        setData(e.target.value)
        setTouch(true)
        if (keyDown) return

        if (globalMU.dataNoFormatoPTEhValida(e.target.value) && e.target.value !== data) {
            props.onChange?.(globalMU.converterDataFormatoPTParaEN(e.target.value))
            setData(e.target.value)
        }

        if (!globalMU.dataNoFormatoPTEhValida(e.target.value)) {
            props.onChange?.(null)
            setData("")
        }

        setKeyDown(false)
    }, [keyDown, data, props.onChange])

    const handleBlur = useCallback((e: React.FocusEvent<HTMLInputElement>) => {
        if (!isMounted.current || !keyDown) return

        setKeyDown(false)
        if (globalMU.dataNoFormatoPTEhValida(e.target.value) && touch) {
            props.onChange?.(globalMU.converterDataFormatoPTParaEN(e.target.value))
            setData(e.target.value)
        }

        if (!globalMU.dataNoFormatoPTEhValida(e.target.value)) {
            props.onChange?.(null)
            setData("")
        }

        if (globalMU.removerMascara("99/99/9999", e.target.value, true).length === 4) {
            const dataString = globalMU.removerMascara("99/99/9999", e.target.value, true)
            const dataPT = `${dataString.substring(0, 2)}/${dataString.substring(2, 4)}/${new Date().getFullYear()}`
            setData(dataPT)
            props.onChange?.(globalMU.converterDataFormatoPTParaEN(dataPT))
        }

        setTouch(false)
    }, [keyDown, touch, props.onChange])

    const handleFocus = useCallback((e: React.FocusEvent<HTMLInputElement>) => {
        if (R.isNil(props.selecionaValorOnFocus) || props.selecionaValorOnFocus) {
            e.target.select()
        }
    }, [props.selecionaValorOnFocus])

    const handleCalendarClick = useCallback(() => {
        setMostrarCalendario(true)
    }, [])

    const handleCalendarClose = useCallback(() => {
        setMostrarCalendario(false)
    }, [])

    const handleDateSelection = useCallback((dataModificada: Date) => {
        if (!isMounted.current) return
        props.onChange?.(globalMU.converterObjetoDateParaDataFormatoEN(dataModificada))
        setData(globalMU.converterObjetoDateParaDataFormatoPT(dataModificada))
        // Fecha o Dialog no tick seguinte, nao no mesmo: o clique no dia ainda esta sendo
        // processado pelo calendario dentro do shadow root, e desmontar a raiz no meio disso
        // interrompe o proprio handler. (Originalmente era contorno para o cleanup de
        // react-modern-calendar-datepicker; a lib saiu na 486834, o adiamento fica porque
        // continua valendo para o handler do clique.)
        setTimeout(() => {
            if (!isMounted.current) return
            setMostrarCalendario(false)
        }, 0)
    }, [props.onChange])

    return (
        <>
            <div className="_flex _flex-row">
                <InputMask
                    mask="99/99/9999"
                    // Normaliza para string|undefined de propósito: o tipo da prop já é `string?`, mas
                    // o TS não protege aqui porque as classes vêm de `require("...scss").default`, que
                    // é `any` — então um chamador escrevendo `className={cond && estilos.x}` passa
                    // `false` sem erro de compilação e o React avisa "Received `false` for a
                    // non-boolean attribute `className`". Este componente é compartilhado por vários
                    // chamadores; barrar aqui evita que o próximo reintroduza o mesmo warning.
                    className={typeof props.className === "string" ? props.className : undefined}
                    disabled={props.disabled}
                    autoComplete="off"
                    aria-autocomplete="none"
                    id={inputId}
                    key={inputId}
                    onKeyDown={handleKeyDown}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    onFocus={handleFocus}
                    value={data}
                    data-testid={inputId}
                />

                {!props.disabled && (
                    <FontAwesomeIcon
                        cursor="pointer"
                        className="_w-3 material-icons"
                        icon={faCalendar}
                        style={{ marginLeft: "-20px" }}
                        onClick={handleCalendarClick}
                        id={iconId}
                        key={iconId}
                        data-testid={iconId}
                    />
                )}
            </div>

            {mostrarCalendario && (
                <Dialog open={mostrarCalendario} onClose={handleCalendarClose} PaperComponent={PaperArrastavel}
                    className={CLASSE_DIALOG_CALENDARIO}
                >
                    <root.div>
                        <Calendario
                            id={`InputData_${inputId}`}
                            key={`InputData_${inputId}`}
                            dataMinima={props.bloquearDataMenorQueHoje ? { year: new Date().getFullYear(), month: new Date().getMonth() + 1, day: new Date().getDate() } : props.dataMinima}
                            dataMaxima={props.dataMaxima ? props.dataMaxima : { year: 2076, month: 12, day: 31 }} // 2077 da erro no calendario
                            data={globalMU.dataNoFormatoPTEhValida(data) ? parse(data, "dd/MM/yyyy", new Date()) : new Date()}
                            onDataModificado={handleDateSelection}
                        />
                    </root.div>
                </Dialog>
            )}
        </>
    )
}